import { capitalize } from '@attain-apps/string-utils'

import { UserFriendlyError } from './user-friendly-error'

/**
 * Enables type-checking that is not affected by having multiple versions of
 * this library installed
 */
// The string here is used as the type description for the type checker
const MARKER_SYMBOL = Symbol.for('@attain-apps/AlreadyExistsError')

/**
 * Some item has been requested to be added or written, but it conflicts with an
 * item that already exists
 *
 * @example
 * // Writing a series of items to a map
 * for (const myItem of myItems) {
 *   if (myMap.has(myItem.key)) {
 *     throw new AlreadyExistsError({ id: myItem.key, type: 'myItem' })
 *   } else {
 *     myMap.set(myItem.key, myItem)
 *   }
 * }
 *
 * // Writing a user to a table
 * try {
 *   await writeToUserTable(user)
 * } catch (err) {
 *   if (err instanceof PgUniqueConstraintViolatedError) {
 *     throw new AlreadyExistsError({ id: user.email, type: 'user' }, err)
 *   }
 *   throw err
 * }
 */
export class AlreadyExistsError extends UserFriendlyError {
  // NOTE: must be all upper case to avoid naming collision with the class name
  public static override readonly NAME: string = 'AlreadyExistsError'
  public override readonly name: string = AlreadyExistsError.NAME

  // Used by TypeChecker
  private readonly [MARKER_SYMBOL] = true as const

  constructor(
    {
      id,
      type = 'item',
      location,
    }: AlreadyExistsError.constructor.Options = {},
    actualError?: Error,
  ) {
    super(
      `${capitalize(type)}${id !== undefined ? ` ${id}` : ''} already exists${location ? ` ${location}` : ''}`,
      actualError,
    )
  }
}
export namespace AlreadyExistsError {
  export namespace constructor {
    export interface Options {
      /**
       * Describes what type of thing we were creating.
       *
       * @default 'item'
       * @example
       * 'rule'
       * 'entry'
       * 'file'
       */
      type?: string
      /**
       * Describes the specific item we were creating.
       *
       * @default not specified
       */
      id?: string | number
      /**
       * Describes where the item already exists.
       *
       * @default not specified
       * @example
       * 'within the system'
       * 'in the database'
       * 'on page 100'
       */
      location?: string
    }
  }

  // NOTE: not using @attain-apps/type-guards as that would introduce circular imports
  export function isAlreadyExistsError(
    val: unknown,
  ): val is AlreadyExistsError {
    return !!(val as AlreadyExistsError)?.[MARKER_SYMBOL]
  }
}
